/**
 * LUXVONO STOREFRONT — Intelligent Multi-Signal Sovereign Country & Language Engine
 * Automatically detects Country, Primary Language, and Currency from IP & Device signals.
 * Performs continuous real-time verification (every 60s, on network change, and on tab focus).
 * Synchronizes with luxvono_geo_context.
 */

export const GEO_STORAGE_KEY = 'luxvono_geo_context';
export const MANUAL_LOCK_KEY = 'luxvono_manual_country_lock';

export const SUPPORTED_LANGUAGES = [
    { code: 'ar', name: 'العربية', nativeName: 'العربية', flag: '🇸🇦', isRTL: true },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', isRTL: false },
    { code: 'so', name: 'Somali', nativeName: 'Af-Soomaali', flag: '🇸🇴', isRTL: false },
    { code: 'am', name: 'Amharic', nativeName: 'አማርኛ (Ethiopia)', flag: '🇪🇹', isRTL: false },
    { code: 'gdq', name: 'Mehri', nativeName: 'اللسان المهري', flag: '🇴🇲', isRTL: true },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', isRTL: false },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', isRTL: false },
    { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', isRTL: false },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', isRTL: false },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', isRTL: false },
    { code: 'zh', name: 'Chinese', nativeName: '简体中文', flag: '🇨🇳', isRTL: false },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', isRTL: false },
    { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', isRTL: false },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', isRTL: false }
];

export function isSupportedLanguage(langCode) {
    if (!langCode) return false;
    const l = String(langCode).toLowerCase().trim();
    return SUPPORTED_LANGUAGES.some(item => item.code.toLowerCase() === l);
}

export function getBestSupportedLanguageForCountry(countryCode) {
    if (!countryCode) return 'ar';
    const code = String(countryCode).toUpperCase().trim();
    const arabCountries = ['SA', 'AE', 'QA', 'KW', 'BH', 'OM', 'YE', 'EG', 'IQ', 'JO', 'LB', 'SY', 'PS', 'LY', 'TN', 'DZ', 'MA', 'SD', 'MR', 'DJ', 'KM'];
    if (arabCountries.includes(code)) return 'ar';
    if (code === 'SO' || code === 'SML') return 'so';
    if (code === 'ET') return 'am';
    if (['DE', 'AT', 'CH'].includes(code)) return 'de';
    if (['FR', 'MC', 'LU', 'BE'].includes(code)) return 'fr';
    if (['ES', 'MX', 'AR', 'CL', 'CO', 'PE'].includes(code)) return 'es';
    if (code === 'IT') return 'it';
    if (code === 'RU') return 'ru';
    if (code === 'TR') return 'tr';
    if (['CN', 'TW', 'HK'].includes(code)) return 'zh';
    if (code === 'JP') return 'ja';
    if (code === 'KR') return 'ko';
    return 'en';
}

export const COUNTRY_DEFAULT_MAPPINGS = {
    SA: { lang: 'ar', curr: 'SAR', name: 'Saudi Arabia' },
    AE: { lang: 'ar', curr: 'AED', name: 'United Arab Emirates' },
    QA: { lang: 'ar', curr: 'QAR', name: 'Qatar' },
    KW: { lang: 'ar', curr: 'KWD', name: 'Kuwait' },
    BH: { lang: 'ar', curr: 'BHD', name: 'Bahrain' },
    OM: { lang: 'ar', curr: 'OMR', name: 'Oman' },
    YE: { lang: 'ar', curr: 'USD', name: 'Yemen' },
    SO: { lang: 'so', curr: 'USD', name: 'Somalia (Mogadishu)' },
    SML: { lang: 'so', curr: 'USD', name: 'Somaliland' },
    ET: { lang: 'am', curr: 'ETB', name: 'Ethiopia' },
    EG: { lang: 'ar', curr: 'EGP', name: 'Egypt' },
    LY: { lang: 'ar', curr: 'LYD', name: 'Libya' },
    TN: { lang: 'ar', curr: 'TND', name: 'Tunisia' },
    DZ: { lang: 'ar', curr: 'DZD', name: 'Algeria' },
    MA: { lang: 'ar', curr: 'MAD', name: 'Morocco' },
    SD: { lang: 'ar', curr: 'USD', name: 'Sudan' },
    MR: { lang: 'ar', curr: 'MRU', name: 'Mauritania' },
    IQ: { lang: 'ar', curr: 'IQD', name: 'Iraq' },
    SY: { lang: 'ar', curr: 'USD', name: 'Syria' },
    PS: { lang: 'ar', curr: 'USD', name: 'Palestine' },
    DJ: { lang: 'ar', curr: 'DJF', name: 'Djibouti' },
    KM: { lang: 'ar', curr: 'KMF', name: 'Comoros' },
    JO: { lang: 'ar', curr: 'JOD', name: 'Jordan' },
    LB: { lang: 'ar', curr: 'USD', name: 'Lebanon' },
    GB: { lang: 'en', curr: 'GBP', name: 'United Kingdom' },
    FR: { lang: 'fr', curr: 'EUR', name: 'France' },
    DE: { lang: 'de', curr: 'EUR', name: 'Germany' },
    IT: { lang: 'it', curr: 'EUR', name: 'Italy' },
    ES: { lang: 'es', curr: 'EUR', name: 'Spain' },
    NL: { lang: 'en', curr: 'EUR', name: 'Netherlands' },
    BE: { lang: 'fr', curr: 'EUR', name: 'Belgium' },
    CH: { lang: 'de', curr: 'CHF', name: 'Switzerland' },
    AT: { lang: 'de', curr: 'EUR', name: 'Austria' },
    IE: { lang: 'en', curr: 'EUR', name: 'Ireland' },
    PT: { lang: 'en', curr: 'EUR', name: 'Portugal' },
    LU: { lang: 'fr', curr: 'EUR', name: 'Luxembourg' },
    MC: { lang: 'fr', curr: 'EUR', name: 'Monaco' },
    SE: { lang: 'en', curr: 'SEK', name: 'Sweden' },
    NO: { lang: 'en', curr: 'NOK', name: 'Norway' },
    DK: { lang: 'en', curr: 'DKK', name: 'Denmark' },
    FI: { lang: 'en', curr: 'EUR', name: 'Finland' },
    IS: { lang: 'en', curr: 'ISK', name: 'Iceland' },
    GR: { lang: 'en', curr: 'EUR', name: 'Greece' },
    CY: { lang: 'en', curr: 'EUR', name: 'Cyprus' },
    MT: { lang: 'en', curr: 'EUR', name: 'Malta' },
    PL: { lang: 'en', curr: 'PLN', name: 'Poland' },
    CZ: { lang: 'en', curr: 'CZK', name: 'Czech Republic' },
    HU: { lang: 'en', curr: 'HUF', name: 'Hungary' },
    RO: { lang: 'en', curr: 'RON', name: 'Romania' },
    BG: { lang: 'en', curr: 'BGN', name: 'Bulgaria' },
    HR: { lang: 'en', curr: 'EUR', name: 'Croatia' },
    SK: { lang: 'en', curr: 'EUR', name: 'Slovakia' },
    SI: { lang: 'en', curr: 'EUR', name: 'Slovenia' },
    EE: { lang: 'en', curr: 'EUR', name: 'Estonia' },
    LV: { lang: 'en', curr: 'EUR', name: 'Latvia' },
    LT: { lang: 'en', curr: 'EUR', name: 'Lithuania' },
    TR: { lang: 'tr', curr: 'TRY', name: 'Turkey' },
    RU: { lang: 'ru', curr: 'RUB', name: 'Russia' },
    US: { lang: 'en', curr: 'USD', name: 'United States' },
    CA: { lang: 'en', curr: 'CAD', name: 'Canada' },
    AU: { lang: 'en', curr: 'AUD', name: 'Australia' },
    CN: { lang: 'zh', curr: 'CNY', name: 'China' },
    JP: { lang: 'ja', curr: 'JPY', name: 'Japan' },
    KR: { lang: 'ko', curr: 'KRW', name: 'South Korea' },
    MY: { lang: 'en', curr: 'MYR', name: 'Malaysia' },
    SG: { lang: 'en', curr: 'SGD', name: 'Singapore' },
    HK: { lang: 'zh', curr: 'HKD', name: 'Hong Kong' },
    NG: { lang: 'en', curr: 'NGN', name: 'Nigeria' },
    ZA: { lang: 'en', curr: 'ZAR', name: 'South Africa' },
    IN: { lang: 'en', curr: 'INR', name: 'India' },
    PK: { lang: 'en', curr: 'PKR', name: 'Pakistan' },
    BD: { lang: 'en', curr: 'BDT', name: 'Bangladesh' },
    TH: { lang: 'en', curr: 'THB', name: 'Thailand' },
    ID: { lang: 'en', curr: 'IDR', name: 'Indonesia' },
    PH: { lang: 'en', curr: 'PHP', name: 'Philippines' },
    VN: { lang: 'en', curr: 'VND', name: 'Vietnam' },
    BR: { lang: 'en', curr: 'BRL', name: 'Brazil' },
    MX: { lang: 'es', curr: 'MXN', name: 'Mexico' },
    AR: { lang: 'es', curr: 'ARS', name: 'Argentina' },
    CL: { lang: 'es', curr: 'CLP', name: 'Chile' },
    CO: { lang: 'es', curr: 'COP', name: 'Colombia' },
    PE: { lang: 'es', curr: 'PEN', name: 'Peru' },
    NZ: { lang: 'en', curr: 'NZD', name: 'New Zealand' },
    KE: { lang: 'en', curr: 'KES', name: 'Kenya' },
    GH: { lang: 'en', curr: 'GHS', name: 'Ghana' }
};

let syncTimer = null;
let isDetecting = false;

/**
 * Execute detection of the true location via IP, SIM, MCC, and telemetry.
 * @param {boolean} force - If true, ignores manual lock and forces live network detection.
 */
export async function detectAndApplyRegionalSettings(force = false) {
    if (typeof window === 'undefined') return null;

    const savedStr = localStorage.getItem(GEO_STORAGE_KEY);
    let currentSaved = null;

    if (savedStr) {
        try {
            currentSaved = JSON.parse(savedStr);
        } catch (_) {}
    }

    // If user has explicitly manually locked their choice and force is not requested, honor the lock
    const isManualLocked = localStorage.getItem(MANUAL_LOCK_KEY) === 'true';
    if (!force && isManualLocked && currentSaved && currentSaved.country) {
        applyDocumentAttributes(currentSaved.language || 'ar');
        return currentSaved;
    }

    if (isDetecting) return currentSaved;
    isDetecting = true;

    // 2. Gather device & browser signals
    let deviceLocale = 'en_US';
    let timeZone = 'UTC';
    try {
        if (typeof navigator !== 'undefined') {
            deviceLocale = navigator.language || (navigator.languages && navigator.languages[0]) || 'en_US';
        }
        if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        }
    } catch (_) {}

    let clientHintCountry = null;
    let clientIp = null;
    const tz = timeZone.toLowerCase();
    const loc = deviceLocale.toLowerCase();

    if (tz.includes('hargeisa') || tz.includes('somaliland')) {
        clientHintCountry = 'SML';
    } else if (tz.includes('mogadishu') || tz.includes('somalia')) {
        clientHintCountry = 'SO';
    } else if (tz.includes('aden') || tz.includes('sanaa') || tz.includes('yemen')) {
        clientHintCountry = 'YE';
    } else if (tz.includes('riyadh') || tz.includes('saudi')) {
        clientHintCountry = 'SA';
    } else if (tz.includes('dubai') || tz.includes('uae')) {
        clientHintCountry = 'AE';
    } else if (tz.includes('muscat') || tz.includes('oman')) {
        clientHintCountry = 'OM';
    } else if (tz.includes('cairo') || tz.includes('egypt')) {
        clientHintCountry = 'EG';
    } else if (tz.includes('qatar') || tz.includes('doha')) {
        clientHintCountry = 'QA';
    } else if (tz.includes('kuwait')) {
        clientHintCountry = 'KW';
    } else if (tz.includes('bahrain')) {
        clientHintCountry = 'BH';
    } else if (tz.includes('casablanca') || tz.includes('morocco')) {
        clientHintCountry = 'MA';
    } else if (tz.includes('algiers') || tz.includes('algeria')) {
        clientHintCountry = 'DZ';
    } else if (tz.includes('tunis') || tz.includes('tunisia')) {
        clientHintCountry = 'TN';
    } else if (tz.includes('tripoli') || tz.includes('libya')) {
        clientHintCountry = 'LY';
    } else if (tz.includes('khartoum') || tz.includes('sudan')) {
        clientHintCountry = 'SD';
    } else if (tz.includes('baghdad') || tz.includes('iraq')) {
        clientHintCountry = 'IQ';
    } else if (tz.includes('amman') || tz.includes('jordan')) {
        clientHintCountry = 'JO';
    } else if (tz.includes('beirut') || tz.includes('lebanon')) {
        clientHintCountry = 'LB';
    } else if (tz.includes('damascus') || tz.includes('syria')) {
        clientHintCountry = 'SY';
    } else if (tz.includes('gaza') || tz.includes('hebron') || tz.includes('jerusalem')) {
        clientHintCountry = 'PS';
    } else if (tz.includes('djibouti')) {
        clientHintCountry = 'DJ';
    } else if (tz.includes('addis_ababa') || tz.includes('ethiopia')) {
        clientHintCountry = 'ET';
    } else if (tz.includes('nouakchott') || tz.includes('mauritania')) {
        clientHintCountry = 'MR';
    } else if (tz.includes('comoro')) {
        clientHintCountry = 'KM';
    } else if (tz.includes('berlin') || tz.includes('busingen') || tz.includes('germany') || loc.startsWith('de')) {
        clientHintCountry = 'DE';
    } else if (tz.includes('istanbul') || tz.includes('ankara') || tz.includes('turkey') || loc.startsWith('tr')) {
        clientHintCountry = 'TR';
    } else if (tz.includes('paris') || tz.includes('france') || loc.startsWith('fr')) {
        clientHintCountry = 'FR';
    } else if (tz.includes('london') || tz.includes('united kingdom')) {
        clientHintCountry = 'GB';
    } else if (tz.includes('amsterdam') || tz.includes('netherlands')) {
        clientHintCountry = 'NL';
    } else if (tz.includes('brussels') || tz.includes('belgium')) {
        clientHintCountry = 'BE';
    } else if (tz.includes('madrid') || tz.includes('spain') || loc.startsWith('es')) {
        clientHintCountry = 'ES';
    } else if (tz.includes('rome') || tz.includes('italy') || loc.startsWith('it')) {
        clientHintCountry = 'IT';
    } else if (tz.includes('zurich')) {
        clientHintCountry = 'CH';
    } else if (tz.includes('vienna') || tz.includes('austria')) {
        clientHintCountry = 'AT';
    } else if (tz.includes('stockholm') || tz.includes('sweden')) {
        clientHintCountry = 'SE';
    } else if (tz.includes('oslo') || tz.includes('norway')) {
        clientHintCountry = 'NO';
    } else if (tz.includes('copenhagen') || tz.includes('denmark')) {
        clientHintCountry = 'DK';
    } else if (tz.includes('helsinki') || tz.includes('finland')) {
        clientHintCountry = 'FI';
    } else if (tz.includes('moscow') || tz.includes('russia') || loc.startsWith('ru')) {
        clientHintCountry = 'RU';
    } else if (tz.includes('tokyo') || tz.includes('japan') || loc.startsWith('ja')) {
        clientHintCountry = 'JP';
    } else if (tz.includes('seoul') || tz.includes('korea') || loc.startsWith('ko')) {
        clientHintCountry = 'KR';
    } else if (tz.includes('shanghai') || tz.includes('china') || loc.startsWith('zh')) {
        clientHintCountry = 'CN';
    } else if (tz.includes('sydney') || tz.includes('melbourne') || tz.includes('australia')) {
        clientHintCountry = 'AU';
    } else if (tz.includes('toronto') || tz.includes('vancouver') || tz.includes('canada')) {
        clientHintCountry = 'CA';
    } else if (tz.includes('new_york') || tz.includes('chicago') || tz.includes('los_angeles') || tz.includes('denver')) {
        clientHintCountry = 'US';
    }

    // Direct Client Public IP Country Detection (Instant Real-World GeoIP with Failover)
    const geoProviders = [
        async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 2500);
            try {
                const r = await fetch('https://api.country.is/', { signal: controller.signal });
                clearTimeout(timer);
                const d = await r.json();
                if (d && d.country && d.country.length === 2) return { country: d.country.trim().toUpperCase(), ip: d.ip };
            } catch (_) {}
            return null;
        },
        async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 2500);
            try {
                const r = await fetch('https://ipwho.is/', { signal: controller.signal });
                clearTimeout(timer);
                const d = await r.json();
                if (d && d.country_code && d.country_code.length === 2) return { country: d.country_code.trim().toUpperCase(), ip: d.ip };
            } catch (_) {}
            return null;
        },
        async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 2500);
            try {
                const r = await fetch('https://freeipapi.com/api/json', { signal: controller.signal });
                clearTimeout(timer);
                const d = await r.json();
                if (d && d.countryCode && d.countryCode.length === 2) return { country: d.countryCode.trim().toUpperCase(), ip: d.ipAddress };
            } catch (_) {}
            return null;
        }
    ];

    for (const provider of geoProviders) {
        try {
            const geoRes = await provider();
            if (geoRes && geoRes.country) {
                clientHintCountry = geoRes.country;
                clientIp = geoRes.ip;
                break;
            }
        } catch (_) {}
    }

    let simCountry = clientHintCountry;
    let mcc = null;
    if (typeof window !== 'undefined' && window.LuxvonoNative) {
        try {
            simCountry = window.LuxvonoNative.getSimCountryCode?.() || clientHintCountry;
            mcc = window.LuxvonoNative.getMcc?.();
        } catch (_) {}
    }

    // 3. Query Sovereign Backend Detection API
    try {
        const response = await fetch('/api/v1/countries/detect', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ip: clientIp,
                networkCountry: clientHintCountry,
                simCountry,
                mcc,
                deviceLocale,
                timeZone
            })
        });

        if (response.ok) {
            const data = await response.json();
            const result = data.data || data;
            if (result && result.countryCode) {
                const detectedCountry = result.countryCode.toUpperCase();
                const mapping = COUNTRY_DEFAULT_MAPPINGS[detectedCountry];
                let detectedLang = result.defaultLanguage || mapping?.lang || 'ar';
                if (!isSupportedLanguage(detectedLang)) {
                    detectedLang = getBestSupportedLanguageForCountry(detectedCountry);
                }
                const detectedCurrency = result.defaultCurrency || mapping?.curr || 'USD';

                // Check for user explicit language preference
                const explicitLang = localStorage.getItem('luxvono_user_explicit_language') || 
                                     (currentSaved && currentSaved.userExplicitLanguage ? currentSaved.language : null);
                
                let effectiveLang = explicitLang ? explicitLang : detectedLang;
                if (!isSupportedLanguage(effectiveLang)) {
                    effectiveLang = getBestSupportedLanguageForCountry(detectedCountry);
                }

                const newGeoContext = {
                    country: detectedCountry,
                    language: effectiveLang,
                    currency: detectedCurrency,
                    userExplicitLanguage: !!explicitLang
                };

                const countryChanged = !currentSaved || currentSaved.country !== detectedCountry;
                const langChanged = !currentSaved || currentSaved.language !== effectiveLang;
                const currChanged = !currentSaved || currentSaved.currency !== detectedCurrency;

                if (countryChanged || langChanged || currChanged) {
                    localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(newGeoContext));
                    if (!explicitLang) {
                        localStorage.setItem('lux_country', detectedCountry);
                        localStorage.setItem('luxvono_user_currency', detectedCurrency);
                    }
                    applyDocumentAttributes(effectiveLang);

                    window.dispatchEvent(new CustomEvent('geoContextChange', { 
                        detail: newGeoContext 
                    }));
                }

                isDetecting = false;
                return newGeoContext;
            }
        }
    } catch (err) {
        console.warn('Live country detection query fallback:', err);
    }

    isDetecting = false;

    if (currentSaved) {
        let explicitLang = localStorage.getItem('luxvono_user_explicit_language') || currentSaved.language || 'ar';
        if (!isSupportedLanguage(explicitLang)) {
            explicitLang = getBestSupportedLanguageForCountry(currentSaved.country);
        }
        applyDocumentAttributes(explicitLang);
        return currentSaved;
    }

    const fallbackCountry = clientHintCountry || 'SA';
    const fallbackMapping = COUNTRY_DEFAULT_MAPPINGS[fallbackCountry] || { lang: 'ar', curr: 'SAR' };
    let fallbackLang = fallbackMapping.lang;
    if (!isSupportedLanguage(fallbackLang)) {
        fallbackLang = getBestSupportedLanguageForCountry(fallbackCountry);
    }
    const fallbackContext = {
        country: fallbackCountry,
        language: fallbackLang,
        currency: fallbackMapping.curr,
        userExplicitLanguage: false
    };
    localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(fallbackContext));
    applyDocumentAttributes(fallbackLang);
    return fallbackContext;
}

/**
 * Starts continuous background synchronization every 5 minutes
 * and whenever the user reconnects to the network or focuses the window.
 */
export function startPeriodicCountrySync(intervalMs = 300000) {
    if (typeof window === 'undefined') return;

    if (syncTimer) {
        clearInterval(syncTimer);
    }

    // Immediate initial sync (respects manual lock)
    detectAndApplyRegionalSettings(false);

    // Periodic background pulse
    syncTimer = setInterval(() => {
        detectAndApplyRegionalSettings(false);
    }, intervalMs);

    // Event-driven real-time refresh hooks
    const handleRecheck = () => {
        detectAndApplyRegionalSettings(false);
    };

    window.addEventListener('online', handleRecheck);
    window.addEventListener('focus', handleRecheck);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            handleRecheck();
        }
    });

    return () => {
        if (syncTimer) clearInterval(syncTimer);
        window.removeEventListener('online', handleRecheck);
        window.removeEventListener('focus', handleRecheck);
    };
}

export function applyDocumentAttributes(langCode) {
    if (typeof document === 'undefined') return;
    const isArabicOrMehri = langCode === 'ar' || langCode === 'gdq' || langCode === 'fa' || langCode === 'ur';
    document.documentElement.dir = isArabicOrMehri ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
}

export function saveUserCountryPreference(countryCode, language, currency, isManual = true) {
    const geoContext = {
        country: countryCode || 'SA',
        language: language || 'ar',
        currency: currency || 'SAR'
    };
    localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(geoContext));
    if (isManual) {
        localStorage.setItem(MANUAL_LOCK_KEY, 'true');
    }
    applyDocumentAttributes(geoContext.language);
    window.dispatchEvent(new Event('languageChange'));
    window.dispatchEvent(new Event('geoContextChange'));
}

export function getUserRegionalPreferences() {
    try {
        const str = localStorage.getItem(GEO_STORAGE_KEY);
        if (str) {
            const parsed = JSON.parse(str);
            if (parsed && parsed.country) return parsed;
        }
    } catch (_) {}
    return {
        country: 'SA',
        language: 'ar',
        currency: 'SAR'
    };
}
